import { ScreenType } from "~/utils/receptionDB.types";
import { ROUTE } from "~/constants/routes";

type SharedErrorConfig = {
  defaultCode: string;
  map: Record<number, string>;
};

const RECEPTIONS_BY_APPOINTMENT_API_ERROR_CONFIG: SharedErrorConfig = {
  defaultCode: "9999",
  map: {
    101: "9001",
    102: "9002",
    103: "9003",
    104: "9004",
    105: "9005",
    10003: "9103",
    10004: "9104",
    10005: "9105",
  },
};

const GENERAL_RECEPTION_API_ERROR_CONFIG: SharedErrorConfig = {
  defaultCode: "8999",
  map: {
    101: "8001",
    102: "8002",
    103: "8003",
    104: "8004",
    105: "8005",
    10004: "8104",
    10006: "8106",
    10007: "8107",
    10008: "8108",
    10009: "8109",
    10010: "8110",
    10011: "8111",
    10012: "8112",
    10013: "8113",
  },
};

/**
 * 画面ごとのエラーコード変換設定。
 */
type ScreenErrorConfig = {
  defaultCode: string;
  map: Record<number, string>;
};

const ERROR_CONFIG: Record<ScreenType, ScreenErrorConfig> = {
  [ScreenType.MENU_RECEPTION]: {
    defaultCode: GENERAL_RECEPTION_API_ERROR_CONFIG.defaultCode,
    map: GENERAL_RECEPTION_API_ERROR_CONFIG.map,
  },
  [ScreenType.RECEPTION_CODE_INPUT]: {
    defaultCode: RECEPTIONS_BY_APPOINTMENT_API_ERROR_CONFIG.defaultCode,
    map: RECEPTIONS_BY_APPOINTMENT_API_ERROR_CONFIG.map,
  },
  [ScreenType.VISITOR_INFO_INPUT]: {
    defaultCode: GENERAL_RECEPTION_API_ERROR_CONFIG.defaultCode,
    map: GENERAL_RECEPTION_API_ERROR_CONFIG.map,
  },
  [ScreenType.DELIVERY_CONFIRMATION]: {
    defaultCode: GENERAL_RECEPTION_API_ERROR_CONFIG.defaultCode,
    map: GENERAL_RECEPTION_API_ERROR_CONFIG.map,
  },
  [ScreenType.QR_SCAN]: {
    defaultCode: RECEPTIONS_BY_APPOINTMENT_API_ERROR_CONFIG.defaultCode,
    map: RECEPTIONS_BY_APPOINTMENT_API_ERROR_CONFIG.map,
  },
  [ScreenType.DEPARTMENT_LIST]: {
    defaultCode: GENERAL_RECEPTION_API_ERROR_CONFIG.defaultCode,
    map: GENERAL_RECEPTION_API_ERROR_CONFIG.map,
  },
  [ScreenType.EMPLOYEE_LIST]: {
    defaultCode: GENERAL_RECEPTION_API_ERROR_CONFIG.defaultCode,
    map: GENERAL_RECEPTION_API_ERROR_CONFIG.map,
  },
};

/**
 * 画面種別とAPIエラーコードから、UIで利用するエラーコード文字列を生成する。
 * エラーコードが未指定、または対応表に存在しない場合は画面ごとのデフォルト値を返す。
 */
export const mapErrorCode = (screen: ScreenType, errorCode?: number): string => {
  const config = ERROR_CONFIG[screen];

  if (!errorCode) {
    return `${screen}${config.defaultCode}`;
  }

  const mapped = config.map[errorCode];

  return `${screen}${mapped ?? config.defaultCode}`;
};

/**
 * UI用エラーコードからエラー画面の遷移先URLを組み立てる。
 */
export const buildErrorPath = (uiCode: string): string => {
  return `${ROUTE.RECEPTION.ERROR}?code=${uiCode}`;
};

/**
 * エラーコードから判定されるエラー種別キー。
 */
export type ErrorResolverKey =
  | "RECEPTION_FAILED"
  | "NO_CALL_DESTINATION"
  | "TARGET_DATA_NOT_FOUND"
  | "UNKNOWN";

type ParsedUiCode = {
  screenCode: string;
  errorCode: number;
};

const parseUiCode = (uiCode: string): ParsedUiCode | null => {
  const trimmed = uiCode.trim();
  if (trimmed.length < 2) return null;

  const numericPart = trimmed.slice(1);
  if (!/^\d+$/.test(numericPart)) return null;

  return {
    screenCode: trimmed.slice(0, 1),
    errorCode: Number.parseInt(numericPart, 10),
  };
};

/**
 * UI用エラーコードの接頭辞から、表示すべきエラー種別を判定する。
 */
export const resolveErrorTypeFromCode = (uiCode: string): ErrorResolverKey => {
  const parsed = parseUiCode(uiCode);
  if (!parsed) {
    return "UNKNOWN";
  }

  const { errorCode } = parsed;

  if (errorCode === 9004 || errorCode === 2003) {
    return "TARGET_DATA_NOT_FOUND";
  }

  if (errorCode > 8000 && errorCode < 10000) {
    return "RECEPTION_FAILED";
  }

  if (errorCode === 2009 || errorCode === 2010) {
    return "NO_CALL_DESTINATION";
  }

  return "UNKNOWN";
};
